import { useEffect, useMemo, useRef } from 'react';
import { assert } from '@varos/util-typescript';
import { buildEditorState } from '@varos/research-editor';
import { ReportingLoaderBaseConfig } from '../../../base';
import { DocumentDetailLoader } from './documentDetailInterface';
import { DocumentDetailAggregate } from './documentDetailModel';
import { replaceEqualDeep } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { Block } from '@varos/research-sdk';
import { buildEditorSuggestion } from './documentDetailFactory';

export function createDocumentDetailLoader(
    config: ReportingLoaderBaseConfig
): DocumentDetailLoader {
    const { repository } = config;

    return {
        useLoad(context, props): DocumentDetailAggregate {
            const contentQuery = repository.research.content.useLookup(
                context,
                { contentId: props.item.id },
                { suspense: true }
            );

            const suggestionQuery = repository.research.suggestion.useFind(
                context,
                { content: props.item.id },
                {
                    suspense: true,
                    select(data) {
                        const [suggestion = null] = data;
                        if (!suggestion) {
                            return null;
                        }
                        if (suggestion.status !== 'pending') {
                            return null;
                        }
                        return suggestion;
                    },
                }
            );

            const lastRef = useRef<Block[]>([]);

            const blockQuery = repository.research.block.useFind(
                context,
                { content: props.item.id },
                {
                    staleTime: Infinity,
                    suspense: true,
                    refetchInterval(data, query) {
                        if (!props.poll) {
                            return false;
                        }
                        return 2500;
                    },
                    select(fresh) {
                        // keep returned value stable unless actual changes
                        // NOTE we need to figure out how to handle server originating updates
                        // like when the agents has produced content
                        if (lastRef.current && isEqual(lastRef.current, fresh)) {
                            return lastRef.current;
                        } else {
                            lastRef.current = fresh;
                            return fresh;
                        }
                    },
                }
            );

            const favoriteQuery = repository.research.favorite.useFind(
                context,
                { content: props.item.id },
                { suspense: true }
            );

            assert(suggestionQuery.status === 'success', 'expected suspense');
            assert(contentQuery.status === 'success', 'expected suspense');
            assert(blockQuery.status === 'success', 'expected suspense');
            assert(favoriteQuery.status === 'success', 'expected suspense');

            const editor = useMemo(() => {
                // console.log('generating editor state', {
                //     block: blockQuery.data,
                //     suggestion: suggestionQuery.data,
                // });
                const state = buildEditorState(blockQuery.data);
                const suggestion = suggestionQuery.data
                    ? buildEditorSuggestion(suggestionQuery.data)
                    : null;

                // console.log('DocumentDetailLoader: built editor suggestion:', {
                //     originalSuggestion: suggestionQuery.data,
                //     convertedSuggestion: suggestion,
                //     operations: suggestion?.operations,
                // });

                return {
                    state,
                    suggestion: suggestion,
                };
            }, [blockQuery.data, suggestionQuery.data]);

            return {
                document: contentQuery.data,
                suggestion: suggestionQuery.data ?? null,
                blocks: blockQuery.data,
                favorite: favoriteQuery.data[0] ?? null,
                item: {
                    content: contentQuery.data,
                },
                author: {
                    id: '1',
                    name: 'Agent Susie',
                },
                editor,
            };
        },
    };
}
